import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Play, Users, Globe, Briefcase, ShieldCheck, Target, TrendingUp, Layers, Server, Infinity as Inf, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useHomeCta } from "../hooks/useHomeCta";
import { openOurStoryModal } from "../components/OurStoryModal";
import { openAgent } from "../utils/openAgent";

const STATS = [
  { icon: Users, v: "150+", l: "People" },
  { icon: Globe, v: "8+", l: "Years of enterprise experience" },
  { icon: Briefcase, v: "100+", l: "Enterprise customers" },
  { icon: ShieldCheck, v: "3", l: "ISO certifications" },
];

const DRIVES = [
  { icon: Target, color: "#9333EA", bg: "#FDF2F8", title: "Business-first", sub: "We start with your goals, not technology." },
  { icon: TrendingUp, color: "#23B2ED", bg: "#E4F7FF", title: "ROI-driven", sub: "We focus on measurable business impact." },
  { icon: Layers, color: "#3B82F6", bg: "#EFF6FF", title: "Technology-agnostic", sub: "We use the best tools for the job." },
  { icon: Server, color: "#10B981", bg: "#ECFDF5", title: "Enterprise-grade", sub: "Built for security, scale and real-world use." },
  { icon: Inf, color: "#06B6D4", bg: "#ECFEFF", title: "Built to evolve", sub: "We partner for the long term." },
];

const LEADERS = [
  {
    img: "/images/about/leader1.png",
    name: "Vineet Saraogi",
    role: "Founder & CEO",
  },
  {
    img: "/images/about/puneet-verma.jpg",
    name: "Puneet Verma",
    role: "Chief Technology Officer",
  },
  {
    img: "/images/about/vinit-gupta.jpg",
    name: "Vinit Gupta",
    role: "Chief Business Officer",
  },
  {
    img: "/images/about/kamal-solanki.jpg",
    name: "Kamal Solanki",
    role: "Operations VP",
  },
];

const CERTS = [
  { img: "iso1", title: "ISO 9001:2015", sub: "Quality Management" },
  { img: "iso2", title: "ISO 27001:2013", sub: "Information Security" },
  { img: "iso3", title: "ISO 14001:2015", sub: "Environmental Management" },
];

const About = () => {
  const navigate = useNavigate();
  const goToHomeCta = useHomeCta();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen w-full bg-white">
      <Navbar />
      <main>
        {/* Panoramic Hero */}
        <section className="relative flex min-h-[88vh] lg:min-h-[640px] xl:min-h-[720px] w-full items-center overflow-hidden" style={{ background: "#010710" }}>
          {/* Panoramic Office Image spanning right side */}
          <div className="absolute inset-y-0 right-0 h-full w-full lg:w-[65%] xl:w-[62%] 2xl:w-[60%] overflow-hidden">
            <img
              src="/images/about/hero.png"
              alt="Simpel AI Modern Office"
              className="h-full w-full object-cover object-center lg:object-left-center"
            />
            {/* Smooth gradient overlay to blend into the dark background on the left */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#010710] via-[#010710]/85 via-25% to-transparent hidden lg:block" />
            {/* Mobile / Tablet dark vignette overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#010710] via-[#010710]/80 to-[#010710]/50 lg:hidden" />
          </div>

          {/* Ambient blue glow in background */}
          <div
            className="pointer-events-none absolute"
            style={{ width: 1000, height: 400, left: -200, top: -80, background: "#173862", filter: "blur(110px)", opacity: 0.55 }}
          />

          {/* Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 py-24 sm:py-28 lg:py-32 lg:px-[70px]">
            <div className="flex max-w-[580px] xl:max-w-[620px] flex-col gap-6">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#8E9CAB]">
                ABOUT SIMPEL AI
              </p>

              <h1 className="text-[44px] sm:text-[56px] lg:text-[62px] xl:text-[66px] font-bold leading-[1.06] text-white tracking-tight">
                Built to turn intelligence into{" "}
                <span className="text-[#00D2FF]">outcomes.</span>
              </h1>

              <div className="flex flex-col gap-2.5">
                <p className="text-[16px] sm:text-[17px] font-bold leading-normal text-white">
                  Simpel AI is The Cognitive AI Company.
                </p>
                <p className="max-w-[496px] text-[15px] sm:text-[16px] leading-[26px] text-white/80">
                  We help businesses understand what AI means for them — and make the transformation happen, with real outcomes.
                </p>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-5 sm:gap-6">
                <button
                  onClick={openAgent}
                  className="flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-[15px] font-semibold text-[#0F172A] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0px_4px_32px_rgba(0,210,255,0.7)]"
                  style={{ boxShadow: "0px 4px 26px rgba(0,194,255,0.45)" }}
                >
                  Discover Your AI Opportunities <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                </button>

                <button
                  onClick={openOurStoryModal}
                  className="group flex items-center gap-3 cursor-pointer"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/35 bg-white/10 backdrop-blur-sm transition-all duration-300 group-hover:bg-white/20 group-hover:scale-105">
                    <Play className="h-4 w-4 fill-white text-white translate-x-0.5" />
                  </span>
                  <span className="text-[15px] font-semibold text-white group-hover:text-white/90">
                    Our Story
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats bar */}
        <section className="w-full border-y border-[#F1F5F9] bg-white">
          <div className="mx-auto max-w-[1440px] px-6 py-7 sm:py-8 lg:px-[70px]">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0 items-center">
              {STATS.map((s, idx) => (
                <div
                  key={s.l}
                  className={`flex items-center gap-4 ${
                    idx !== 0 ? "lg:border-l lg:border-[#E2E8F0] lg:pl-10 xl:pl-12" : ""
                  }`}
                >
                  <span className="flex h-11 w-11 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#2563EB]">
                    <s.icon className="h-5 w-5 sm:h-5.5 sm:w-5.5" strokeWidth={2.2} />
                  </span>
                  <div className="flex flex-col">
                    <div className="text-[28px] sm:text-[34px] font-bold leading-none text-[#0F172A] tracking-tight">
                      {s.v}
                    </div>
                    <div className="mt-1 text-[12.5px] sm:text-[13px] font-medium leading-tight text-[#64748B]">
                      {s.l}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Belief + What drives us */}
        <section className="w-full" style={{ background: "#F5FAFE" }}>
          <div className="mx-auto max-w-[1440px] px-6 py-16 lg:px-[63px]">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-[702px]">
                <p className="text-[11px] font-bold uppercase tracking-[1.1px] text-[#94A3B8]">OUR BELIEF</p>
                <h2 className="mt-2 text-[26px] font-bold leading-[37px] text-[#020617] sm:text-[30px]">Every business is becoming an AI business.</h2>
                <p className="mt-2 text-[14px] leading-5 text-[#475569]">Our role is to help you turn that possibility into a practical, measurable reality.</p>
              </div>
              <p className="max-w-[423px] border-l-[2px] border-[#E2E8F0] pl-6 text-[14px] leading-[22px] text-[#475569]">We combine business understanding, AI expertise and execution strength to help organisations discover opportunities, build solutions and continuously create value.</p>
            </div>
            <p className="mt-14 text-[11px] font-bold uppercase tracking-[1.1px] text-[#94A3B8]">WHAT DRIVES US</p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {DRIVES.map((d) => (
                <div key={d.title} className="rounded-[16px] border border-[#F1F5F9] bg-white p-6 transition-shadow hover:shadow-[0_8px_24px_rgba(15,23,42,0.06)]">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: d.bg }}>
                    <d.icon className="h-5 w-5" style={{ color: d.color }} strokeWidth={2} />
                  </span>
                  <h3 className="mt-4 text-[16px] font-bold text-[#0F172A]">{d.title}</h3>
                  <p className="mt-1 text-[13.5px] leading-5 text-[#64748B]">{d.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Leadership Section */}
        <section className="w-full border-t border-[#F1F5F9] bg-white py-20 lg:py-24">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col gap-10 lg:flex-row lg:gap-8 xl:gap-10">
              
              {/* Left Content Column */}
              <div className="flex flex-col justify-start lg:w-[320px] xl:w-[350px] lg:shrink-0 lg:pt-1">
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2px] text-[#8E9CAB]">
                  OUR LEADERSHIP
                </p>
                <h2 className="mt-3 text-[26px] sm:text-[30px] lg:text-[32px] font-bold leading-[1.2] text-[#0F172A] tracking-tight">
                  <span className="block whitespace-nowrap">Experienced leaders.</span>
                  <span className="block whitespace-nowrap">A shared mission.</span>
                </h2>
                <p className="mt-4 text-[13.5px] sm:text-[14px] leading-[22px] text-[#64748B]">
                  <span className="block">A diverse team with deep experience in</span>
                  <span className="block">enterprise technology, product engineering and AI.</span>
                </p>
              </div>

              {/* Right Cards Grid: 4 Leaders + 1 "150+" Box */}
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5 flex-1 lg:gap-5">
                {LEADERS.map((m) => (
                  <div key={m.name} className="flex flex-col">
                    <img
                      src={m.img}
                      alt={m.name}
                      className="aspect-square w-full rounded-[20px] object-cover shadow-sm border border-slate-100"
                    />
                    <div className="mt-3 text-[15px] font-bold text-[#0F172A] leading-tight">
                      {m.name}
                    </div>
                    <div className="text-[12.5px] text-[#64748B] mt-0.5">
                      {m.role}
                    </div>
                    {/* Social Media Icons row matching Screenshot */}
                    <div className="mt-3 flex items-center gap-2.5 text-[#94A3B8]">
                      {/* LinkedIn Icon with clean rounded badge & white cutout */}
                      <a
                        href="#linkedin"
                        aria-label={`${m.name} LinkedIn`}
                        className="transition-colors hover:text-[#007AFF]"
                      >
                        <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24">
                          <rect width="22" height="22" x="1" y="1" rx="4" fill="currentColor" />
                          <path
                            fill="white"
                            d="M7.7 19H5.2V10.5H7.7V19ZM6.45 9.35C5.65 9.35 5 8.7 5 7.9C5 7.1 5.65 6.45 6.45 6.45C7.25 6.45 7.9 7.1 7.9 7.9C7.9 8.7 7.25 9.35 6.45 9.35ZM19 19H16.5V14.6C16.5 13.55 16.1 12.85 15.15 12.85C14.4 12.85 13.95 13.35 13.75 13.85C13.7 14 13.7 14.25 13.7 14.5V19H11.2C11.2 19 11.23 11.2 11.2 10.5H13.7V11.7C14.05 11.15 14.85 10.35 16.3 10.35C17.95 10.35 19 11.45 19 13.8V19Z"
                          />
                        </svg>
                      </a>
                      {/* X (Twitter) Icon */}
                      <a
                        href="#x"
                        aria-label={`${m.name} X`}
                        className="transition-colors hover:text-[#007AFF]"
                      >
                        <svg className="w-[18px] h-[18px] fill-current" viewBox="0 0 24 24">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      </a>
                      {/* Facebook Icon */}
                      <a
                        href="#facebook"
                        aria-label={`${m.name} Facebook`}
                        className="transition-colors hover:text-[#007AFF]"
                      >
                        <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="11" fill="currentColor" />
                          <path
                            fill="white"
                            d="M13.5 19V12.5H15.5L15.8 10H13.5V8.4C13.5 7.7 13.7 7.2 14.7 7.2H16V5C15.8 4.95 14.9 4.85 13.9 4.85C11.7 4.85 10.2 6.2 10.2 8.6V10H8V12.5H10.2V19H13.5Z"
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                ))}
                
                {/* 150+ Box */}
                <div className="flex flex-col justify-between rounded-[20px] bg-[#F0F7FF] border border-[#E0EFFF] p-5 sm:p-6 shadow-sm">
                  <div>
                    <div className="text-[36px] sm:text-[40px] font-bold leading-none text-[#007AFF]">
                      150+
                    </div>
                    <div className="mt-3 text-[13px] leading-[20px] text-[#475569]">
                      curious minds building a more capable tomorrow.
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      navigate("/careers");
                      window.scrollTo(0, 0);
                    }}
                    className="mt-6 flex items-center gap-1.5 text-[13px] font-semibold text-[#007AFF] hover:underline cursor-pointer"
                  >
                    Join our team <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
              
            </div>
          </div>
        </section>

        {/* Trust & Certifications */}
        <section className="w-full bg-[#010A16] py-20 lg:py-24">
          <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-12 px-6 lg:grid-cols-12 lg:gap-8 xl:gap-12 lg:px-[70px]">
            {/* Left Column (4 cols) */}
            <div className="flex flex-col lg:col-span-4">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2px] text-[#8E9CAB]">
                TRUST & CERTIFICATIONS
              </p>
              <h2 className="mt-3 text-[32px] sm:text-[38px] font-bold leading-[1.15] text-white">
                Built on trust.<br />Certified for excellence.
              </h2>
              <p className="mt-4 text-[14px] sm:text-[14.5px] leading-[22px] text-[#94A3B8] max-w-[380px]">
                We follow global standards for quality, information security and environmental management.
              </p>
            </div>

            {/* Middle Column: 3 ISO Cards (5 cols) */}
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 sm:gap-5 lg:col-span-5">
              {CERTS.map((c) => (
                <div key={c.title} className="flex flex-col items-center">
                  <div className="flex h-[180px] w-[140px] sm:w-[148px] flex-col items-center justify-center rounded-[18px] bg-white p-4 shadow-lg text-center">
                    <img
                      src={`/images/about/${c.img}.png`}
                      alt={c.title}
                      className="h-[84px] w-[84px] object-contain"
                    />
                    <div className="mt-3 text-[12.5px] font-bold leading-tight text-[#0F172A]">
                      {c.title}
                    </div>
                    <div className="mt-0.5 text-[9.5px] leading-tight text-[#64748B]">
                      {c.sub}
                    </div>
                  </div>
                  <span className="mt-2.5 text-[11px] font-medium text-[#64748B]">
                    Cert No. XXXXX
                  </span>
                </div>
              ))}
            </div>

            {/* Right Column: Customer Testimonial White Card (3 cols) */}
            <div className="rounded-[20px] bg-white p-7 sm:p-8 shadow-xl lg:col-span-3">
              <div className="text-[42px] leading-none text-[#007AFF] font-serif font-bold">
                “
              </div>
              <p className="mt-2 text-[13.5px] sm:text-[14px] leading-[23px] text-[#334155]">
                Simpel AI combines deep technical expertise with a strong understanding of business realities. They are a trusted partner in our transformation journey.
              </p>
              <p className="mt-5 text-[12px] font-medium text-[#64748B]">
                — Customer, Professional Services
              </p>
            </div>
          </div>
        </section>

        {/* Global presence matching Screenshot 1 */}
        <section className="w-full border-t border-[#F1F5F9] bg-white py-16 sm:py-20 lg:py-24">
          <div className="mx-auto flex max-w-[1440px] flex-col lg:flex-row items-center justify-between gap-10 lg:gap-12 xl:gap-16 px-6 lg:px-[70px]">
            
            {/* Left Content Column */}
            <div className="flex flex-col justify-start w-full lg:w-[320px] xl:w-[350px] lg:shrink-0">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2px] text-[#8E9CAB]">
                OUR GLOBAL PRESENCE
              </p>
              <h2 className="mt-3 text-[32px] sm:text-[38px] lg:text-[42px] font-bold leading-[1.1] text-[#0F172A] tracking-tight">
                Local teams.<br />Global outlook.
              </h2>
              <p className="mt-4 text-[13.5px] sm:text-[14px] leading-[22px] text-[#64748B] max-w-[340px]">
                From India to the world, we work with enterprises across geographies, bringing domain understanding and global delivery capabilities.
              </p>
            </div>

            {/* Right Map Image */}
            <div className="flex-1 w-full flex items-center justify-center lg:justify-end">
              <img
                src="/images/about/global-presence-map.png"
                alt="Simpel AI Global Presence Map - North America, UK & Europe, India, Middle East, APAC, Australia"
                className="w-full max-w-[940px] xl:max-w-[1000px] object-contain select-none"
              />
            </div>
            
          </div>
        </section>

        {/* Careers CTA */}
        <section id="cta" className="relative w-full overflow-hidden" style={{ minHeight: 320 }}>
          <img src="/images/about/cta.png" alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #04101D 0%, rgba(7,22,38,0.3) 50%, rgba(8,42,89,0) 100%)" }} />
          <div className="relative mx-auto max-w-[1440px] px-6 py-20 lg:px-[75px]">
            <div className="flex max-w-[620px] flex-col gap-4">
              <p className="text-[12px] font-semibold uppercase tracking-[3.36px] text-[#F8FBFF]">CAREERS AT SIMPEL AI</p>
              <h2 className="text-[28px] font-semibold text-[#F8FBFF] sm:text-[32px]">Want to build the Cognitive Enterprise with us?</h2>
              <button
                onClick={() => {
                  navigate("/careers");
                  window.scrollTo(0, 0);
                }}
                className="mt-2 flex w-fit items-center gap-2 rounded-full bg-white px-7 py-[14px] text-[14px] font-semibold text-[#0F172A] transition-transform hover:scale-[1.03] cursor-pointer"
                style={{ boxShadow: "0px 4px 21px rgba(47,115,246,0.73)" }}
              >
                Explore Careers <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default About;
